import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import DoneIcon from '@mui/icons-material/Done';
import {
    Box,
    Button,
    TextField,
    Typography,
    Slide,
    Skeleton,
} from '@mui/material';
import { t } from 'i18next';
import React, { memo, useCallback, useEffect, useRef, useState } from 'react';

import { useOrganisationsContext } from '@/context/OrganisationsContext';
import { useOrganisationInvitation } from '@/hooks';
import Logger from '@/services/logger';
import { OrganisationsAPI } from '@/services/networking/organisations';
import { Snackbar } from '@/services/toastNotifications';
import { COLOR } from '@/theme/colors';

const TEXT_FIELD_HEIGHT = 48;

const InvitationUrlSection = ({
    isDisabled = false,
}: {
    isDisabled: boolean;
}) => {
    const { invitationUrl, invitationId } = useOrganisationInvitation();
    const { fetchMembers, organisationActiveInvitations } =
        useOrganisationsContext();

    const [linkCopied, setLinkCopied] = useState(false);
    const containerRef = useRef(null);

    useEffect(() => {
        if (linkCopied) {
            const timer = setTimeout(() => {
                setLinkCopied(false);
            }, 2000);

            return () => clearTimeout(timer);
        }
    }, [linkCopied]);

    const copyLink = useCallback(async () => {
        try {
            const isAlreadyActive = organisationActiveInvitations.some(
                (invitation) => invitation.id === invitationId,
            );

            if (invitationId && !isAlreadyActive) {
                await OrganisationsAPI.activateInvitation({ invitationId });

                await fetchMembers();
            }

            navigator.clipboard.writeText(invitationUrl || '');

            setLinkCopied(true);
        } catch (err) {
            Logger.logError(err);
            Snackbar.showToastNotification({
                message: t('App:Messages:something_went_wrong'),
                options: {
                    variant: 'error',
                },
            });
        }
    }, [
        organisationActiveInvitations,
        invitationId,
        invitationUrl,
        fetchMembers,
    ]);

    const companyNameFieldTrailingContent = (
        <Button
            onClick={copyLink}
            disabled={isDisabled}
            variant="outlined"
            sx={{
                width: 120,
                height: 34,
                color: COLOR.black,
                borderColor: COLOR.black,
                '&:hover': {
                    backgroundColor: COLOR.white,
                    borderColor: COLOR.black,
                },
                '& .MuiButton-startIcon': { marginRight: '4px' },
            }}
            startIcon={<ContentCopyIcon />}
        >
            <Typography sx={{ fontSize: 10, fontWeight: 700 }}>
                {t('TeamInvitationModal:buttons:copy_link')}
            </Typography>
        </Button>
    );

    return (
        <>
            <Box className="flex gap-2 mt-6 min-w-[764px]" ref={containerRef}>
                {invitationUrl ? (
                    <TextField
                        data-test-id="TeamInvitationModal-LinkInput"
                        id="invitationLink"
                        InputProps={{
                            endAdornment: companyNameFieldTrailingContent,
                            style: {
                                height: TEXT_FIELD_HEIGHT,
                                fontSize: 14,
                                fontWeight: 300,
                                color: '#212B36',
                            },
                            readOnly: true,
                        }}
                        InputLabelProps={{ shrink: true }}
                        name="invitationLink"
                        sx={{
                            width: 518,
                            borderColor: '#e5e7eb',
                            '& .MuiOutlinedInput-root': {
                                '&:hover fieldset': {
                                    borderColor: '#e5e7eb',
                                },
                            },
                        }}
                        focused={false}
                        value={invitationUrl || ''}
                    />
                ) : (
                    <Skeleton
                        height={48}
                        sx={{ borderRadius: 1 }}
                        variant="rectangular"
                        width={518}
                    />
                )}

                <Slide
                    direction="up"
                    in={linkCopied}
                    container={containerRef.current}
                >
                    <Button
                        variant="text"
                        className="self-center"
                        sx={{
                            width: 150,
                            height: 30,
                            backgroundColor: COLOR.white,
                            boxShadow: '0px 3px 6px #00000029',
                            border: '1px solid #2E46AD33',
                            opacity: 0.86,
                            backdropFilter: 'blur(7px)',
                            WebkitBackdropFilter: 'blur(7px)',
                            '&:hover': {
                                backgroundColor: COLOR.white,
                            },
                        }}
                        disabled
                        startIcon={
                            <DoneIcon
                                fontSize="small"
                                sx={{ color: COLOR.black }}
                            />
                        }
                    >
                        <Typography
                            sx={{
                                fontSize: 10,
                                fontWeight: 400,
                                color: COLOR.black,
                            }}
                        >
                            {t(
                                'TeamInvitationModal:labels:copied_to_clickboard',
                            )}
                        </Typography>
                    </Button>
                </Slide>
            </Box>
            {!isDisabled && (
                <Box className="mt-4">
                    <Typography
                        sx={{
                            fontSize: 14,
                            fontWeight: 300,
                            color: '#212B36',
                            maxWidth: 500,
                        }}
                    >
                        {t('TeamInvitationModal:description1')}
                    </Typography>
                </Box>
            )}
        </>
    );
};

export default memo(InvitationUrlSection);
